import { useState } from 'react';
import { toast } from 'sonner';
import { framesToPrepare, prepareFrames } from '../../lib/treatmentPdf';

/**
 * Prepare a treatment's frames in the browser, then hand them to `download`.
 * Shared by the owner's Export PDF dialog and the shared link's download -
 * they differ only in which endpoint `download(images)` posts to.
 *
 * `stage` is null when idle, { done, total } while frames are prepared, and
 * 'building' while the server makes the PDF.
 */
export function useTreatmentPdfExport(shots, download) {
  const [stage, setStage] = useState(null);

  const run = async () => {
    try {
      setStage({ done: 0, total: framesToPrepare(shots).length });
      const { images, failed } = await prepareFrames(shots, (done, total) => setStage({ done, total }));
      setStage('building');
      await download(images);
      toast.success('PDF downloaded');
      if (failed) toast(`${failed} frame${failed === 1 ? '' : 's'} couldn't be loaded and print as a link instead`);
      return true;
    } catch (e) {
      toast.error(e.response?.status === 429 ? 'Too many downloads - try again in a minute' : 'Could not create the PDF');
      return false;
    } finally {
      setStage(null);
    }
  };

  const percent = stage === null ? 0 : stage === 'building' ? 100 : stage.total ? (stage.done / stage.total) * 100 : 100;
  const label = stage === 'building' ? 'Building the PDF…' : stage ? `Preparing frames ${stage.done} of ${stage.total}…` : '';
  return { run, busy: stage !== null, percent, label };
}
